/**
 * Cloud Functions Service
 * VireoHR Cloud Functions client-side calls
 * Region: me-central1 (Doha)
 */

import { getFunctions, httpsCallable, connectFunctionsEmulator } from 'firebase/functions';

import { app } from '@/lib/firebase/client';

// Initialize Functions with correct region
export const functions = getFunctions(app, 'me-central1');

// Connect to emulator in development
if (import.meta.env.DEV && import.meta.env.VITE_USE_EMULATORS === 'true') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
}

// Type definitions for Cloud Function calls

export interface RecordAttendanceInput {
  tenantId: string;
  scheduleId: string;
  branchId: string;
  coords: { lat: number; lng: number };
  deviceId: string;
  clientTimestamp: number;
}

export interface RecordAttendanceOutput {
  status: 'CLOCKED_IN' | 'ALREADY_CLOCKED_IN';
  attendance: {
    id: string;
    tenantId: string;
    employeeId: string;
    scheduleId: string;
    branchId: string;
    clockInTimestamp: string;
    clockOutTimestamp: string | null;
    status: 'ON_TIME' | 'LATE' | 'NO_SHOW';
    lateMinutes?: number;
  };
  distance?: number;
  message?: string;
}

export interface ClockOutInput {
  tenantId: string;
  coords: { lat: number; lng: number };
  deviceId: string;
  clientTimestamp: number;
}

export interface ClockOutOutput {
  status: 'CLOCKED_OUT' | 'NO_ACTIVE_SHIFT' | 'ALREADY_CLOCKED_OUT';
  attendance?: {
    id: string;
    clockOutTimestamp: string;
    hoursWorked: number;
  };
  attendanceId?: string;
  hoursWorked?: number;
  distance?: number;
  message?: string;
}

export interface CreateLeaveRequestInput {
  tenantId: string;
  employeeId: string;
  employeeName: string;
  date: string;
  endDate?: string;
  type?: 'sick' | 'vacation' | 'personal' | 'other';
  reason: string;
}

export interface CreateLeaveRequestOutput {
  success: boolean;
  requestId: string;
  message: string;
}

export interface ApproveLeaveRequestInput {
  requestId: string;
  approve: boolean;
  rejectionReason?: string;
}

export interface ApproveLeaveRequestOutput {
  success: boolean;
  status: 'approved' | 'rejected';
  message: string;
}

export interface GeneratePayrollInput {
  tenantId: string;
  periodId?: string; // YYYY-MM format
}

export interface GeneratePayrollOutput {
  success: boolean;
  periodId: string;
  employeeCount: number;
  recordsGenerated: number;
  message: string;
}

// Cloud Function wrappers

export const recordAttendance = httpsCallable<RecordAttendanceInput, RecordAttendanceOutput>(
  functions,
  'recordAttendance'
);

export const clockOut = httpsCallable<ClockOutInput, ClockOutOutput>(
  functions,
  'clockOut'
);

export const createLeaveRequest = httpsCallable<CreateLeaveRequestInput, CreateLeaveRequestOutput>(
  functions,
  'createLeaveRequest'
);

export const approveLeaveRequest = httpsCallable<ApproveLeaveRequestInput, ApproveLeaveRequestOutput>(
  functions,
  'approveLeaveRequest'
);

export const generatePayroll = httpsCallable<GeneratePayrollInput, GeneratePayrollOutput>(
  functions,
  'generatePayrollForPeriodEnhanced'
);

// NEW VireoHR Cloud Functions

export interface CreateEmployeeInput {
  tenantId: string;
  email: string;
  name: string;
  phone?: string;
  role?: string;
  isManager?: boolean;
  managerLocationIds?: string[];
  salaryType: 'hourly' | 'salary';
  hourlyRate?: number;
  monthlySalary?: number;
  expectedHours?: number;
  locationIds?: string[];
}

export interface CreateEmployeeOutput {
  success: boolean;
  employeeId: string;
  email: string;
  defaultPassword: string;
  message: string;
}

export interface UpdateEmployeeInput {
  employeeId: string;
  updates: Partial<CreateEmployeeInput>;
}

export interface UpdateEmployeeOutput {
  success: boolean;
  message: string;
}

export interface ExportPayrollCSVInput {
  tenantId: string;
  month: number;
  year: number;
}

export interface ExportPayrollCSVOutput {
  success: boolean;
  csv: string;
  filename: string;
}

export interface CreateScheduleInput {
  tenantId: string;
  branchId: string;
  employeeIds: string[];
  startTimestamp: string;
  endTimestamp: string;
}

export interface CreateScheduleOutput {
  success: boolean;
  scheduleId: string;
  message: string;
}

export const createEmployee = httpsCallable<CreateEmployeeInput, CreateEmployeeOutput>(
  functions,
  'createEmployee'
);

export const updateEmployee = httpsCallable<UpdateEmployeeInput, UpdateEmployeeOutput>(
  functions,
  'updateEmployee'
);

export const exportPayrollCSV = httpsCallable<ExportPayrollCSVInput, ExportPayrollCSVOutput>(
  functions,
  'exportPayrollCSV'
);

export const createSchedule = httpsCallable<CreateScheduleInput, CreateScheduleOutput>(
  functions,
  'createSchedule'
);

export interface AdminCreateTenantInput {
  businessName: string;
  ownerName: string;
  ownerEmail: string;
  plan: 'trial' | 'basic' | 'premium';
}

export interface AdminCreateTenantOutput {
  success: boolean;
  tenantId: string;
  ownerUid: string;
  isNewUser: boolean;
  tempPassword?: string;
  message: string;
}

export const adminCreateTenant = httpsCallable<AdminCreateTenantInput, AdminCreateTenantOutput>(
  functions,
  'adminCreateTenant'
);

export const updateSystemPricing = httpsCallable<{ basePrice: number; pricePerEmployee: number; includedEmployees: number }, { success: true }>(
  functions,
  'updateSystemPricing'
);

export const updateGlobalSettings = httpsCallable<{ expectedMonthlyHours: string }, { success: boolean; message: string }>(
  functions,
  'updateGlobalSettings'
);

export const sendSystemAnnouncement = httpsCallable<{ message: string }, { success: true }>(
  functions,
  'sendSystemAnnouncement'
);

export const sendAdminPushNotification = httpsCallable<{ title: string; body: string }, { success: true }>(
  functions,
  'sendAdminPushNotification'
);

export const suspendTenant = httpsCallable<{ tenantId: string; suspend: boolean }, { success: true }>(
  functions,
  'suspendTenant'
);
